import { action, computed, observable } from 'mobx';
import { mergeWith } from 'lodash';
import { Flow } from '../models/flow.model';
import { Page } from '../models/page.model';
import { Variable } from '../models/variable.model';
import {
    TCommand,
    ICreateCommand,
    IDeleteCommand,
    IUpdateCommand,
    DataHandleMethod,
    TBlock
} from '../types/builder.types';
import {
    IEnrichedFlowTemplate,
    IFlow,
    IFlowTheme,
    RevisionType
} from '../types/model.types';
import {
    TBlockResponse,
    IVariableResponse,
    ITransformedPageResponse,
    IPageResponse,
    IConfiguration,
    ITransformedFlowResponse
} from '../types/server.types';
import type { RootStore } from './root.store';
import { commandsByType } from '../utils/commands.utils';
import { FlowRevision } from '../models/flow-revision.model';
import { safeMergeValue } from '../utils/data.utils';
import { createBlockInstance } from '~/utils/block.utils';

export const DEFAULT_FLOW_THEME: IFlowTheme = {
    styles: {},
    buttons_styles: {},
    questions_styles: {},
    heading_styles: {}
};

export const DEFAULT_PAGE_THEME = {
    styles: {
        background_color: '#ffffff',
        background_color_opacity: 0,
        opacity: 100
    }
};

interface ISyncFlowWithServerResponseOptions {
    revisionType?: RevisionType;
    revision_id?: string;
    revision_count?: number;
    recreateBlocks?: boolean;
}

const syncFlowWithServerResponseDefaults = {
    revisionType: RevisionType.Draft,
    recreateBlocks: false
};

export class FlowStore {
    @observable flows: Record<string, Flow> = {};
    @observable templates: Record<string, IEnrichedFlowTemplate> = {};

    rootStore: RootStore = null;

    constructor(rootStore: RootStore) {
        this.rootStore = rootStore;
    }

    @computed
    get templatesList() {
        return Object.values(this.templates);
    }

    @action
    init() {
        this.flows = {};
    }

    loadTransformedFlowResponse(
        response: ITransformedFlowResponse,
        isDraft = true
    ) {
        this.load(
            response.flow,
            response.pages,
            response.blocks,
            response.variables,
            isDraft
        );
    }

    @action
    load(
        flow: IFlow,
        pages: Record<string, ITransformedPageResponse>,
        blocks: Record<string, TBlockResponse>,
        variables: Record<string, IVariableResponse>,
        isDraft = true
    ) {
        const flowRevision = new FlowRevision(
            flow.revision_id,
            flow._id,
            pages,
            blocks,
            variables,
            this.rootStore
        );

        // For active flows, remove published_at and last_changed_at
        // since they overwrite the isDirty state (which referes to draft)
        let transformedFlow;
        if (isDraft) {
            const { variables: variablesNotInUse, ...rest } = flow;

            transformedFlow = rest;
        } else {
            const {
                published_at,
                last_changed_at,
                variables: variablesNotInUse,
                theme,
                ...rest
            } = flow;
            transformedFlow = rest;
        }

        if (!this.flows[flow._id]) {
            this.flows[flow._id] = new Flow(transformedFlow, this.rootStore);
        } else {
            this.updateFlow(flow._id, <Partial<Flow>>transformedFlow);
        }

        const newFlow = this.flows[flow._id];
        if (isDraft) {
            newFlow.setDraftRevision(flowRevision);
        } else {
            newFlow.setActiveRevision(flowRevision);
        }
        return newFlow;
    }

    @action
    syncFlowWithServerResponse(
        flow: Flow,
        pages: ITransformedPageResponse[] | IPageResponse[],
        blocks: TBlockResponse[],
        variables: IVariableResponse[],
        {
            revisionType = syncFlowWithServerResponseDefaults.revisionType,
            revision_id,
            revision_count,
            recreateBlocks
        }: ISyncFlowWithServerResponseOptions = syncFlowWithServerResponseDefaults
    ) {
        const isNewDraftRevisionWasCreatedInTheServer =
            flow.activeRevision &&
            revisionType === RevisionType.Draft &&
            flow.draftRevision._id !== revision_id &&
            flow.draftRevision._id === flow.activeRevision._id;
        if (revision_id && isNewDraftRevisionWasCreatedInTheServer) {
            flow.draftRevision._id = revision_id;
        }

        flow.revision_count = revision_count;

        const revision =
            revisionType === RevisionType.Active
                ? flow.activeRevision
                : flow.draftRevision;

        if (recreateBlocks) {
            revision.blocksMap = {};
            (blocks || []).forEach(block => {
                const newBlock = createBlockInstance(
                    block,
                    revision,
                    this.rootStore
                );

                revision.addBlock(newBlock);
            });
        } else {
            (blocks || []).forEach(block => {
                const existingBlock = revision.blocksMap[block.block_id];
                if (!existingBlock) {
                    return;
                }

                existingBlock.mergeBlockAttributes(block, {
                    connected_variable_id: true
                });
            });
        }

        (pages || []).forEach(pageResponse => {
            const page = revision.pagesMap[pageResponse.page_id];
            if (!page) {
                return;
            }

            page.mergeAttributes(pageResponse);
        });

        flow.updateVariables(variables, revisionType);
    }

    @action
    updateFlow(flowId: string, data: Partial<Flow>) {
        // when fetching active flow - theme is removed
        // when merging active flow with active flow, we need to have a theme attribute in the current flow object,
        // in order to allow the merge
        this.flows[flowId]._theme =
            this.flows[flowId]._theme || DEFAULT_FLOW_THEME;

        this.flows[flowId] = mergeWith(
            this.flows[flowId],
            data,
            // eslint-disable-next-line consistent-return
            (objValue, srcValue, key) => {
                if (Array.isArray(objValue) && key === 'related_flows') {
                    return srcValue;
                }
            }
        );
    }

    // Please add all actions above this one
    @action
    setConfigurationData(
        flowId: string,
        data: Partial<IConfiguration>,
        dataHandleMethod: DataHandleMethod = DataHandleMethod.merge
    ) {
        this.flows[flowId].configuration =
            dataHandleMethod === DataHandleMethod.merge
                ? safeMergeValue(this.flows[flowId].configuration, data)
                : Object.assign(this.flows[flowId].configuration, data);
    }

    @action
    applyBulkActions(flowId, commands: TCommand[]) {
        const {
            actionLoggerService: { actionLog }
        } = this.rootStore;

        const flow = this.flows[flowId];
        const flowRevision = flow.draftRevision;

        const { createCommands, updateCommands, deleteCommands } =
            commandsByType(commands);

        // Create
        createCommands.forEach(({ data }: ICreateCommand) => {
            const cloneData = data.clone(); // We want to clone because we don't want to alter the data inside the commands.

            if (data instanceof Page) {
                flowRevision.addPage(cloneData);

                actionLog?.addCommandData(
                    'page',
                    'create',
                    (cloneData as Page).page_id,
                    (data as Page).title
                );
            } else {
                flowRevision.addBlock(cloneData);

                actionLog?.addCommandData(
                    'block',
                    'create',
                    (cloneData as TBlock).block_id,
                    (cloneData as TBlock).type
                );
            }
        });

        // Update
        updateCommands.forEach(({ data, dataHandleMethod }: IUpdateCommand) => {
            if ('block_id' in data) {
                flowRevision.setBlockData(
                    data.block_id,
                    data,
                    dataHandleMethod
                );

                actionLog?.addCommandData(
                    'block',
                    'update',
                    data.block_id,
                    flowRevision.blocksMap[data.block_id]?.type
                );
            } else if ('page_id' in data) {
                flowRevision.updatePage(data.page_id, <Partial<Page>>data);

                actionLog?.addCommandData(
                    'page',
                    'update',
                    data.page_id,
                    flowRevision.pagesMap[data.page_id]?.title
                );
            } else if ('variable_id' in data) {
                flowRevision.setVariableValue(
                    data.variable_id,
                    <Partial<Variable>>data.value
                );

                const variable = flowRevision.variablesMap[data.variable_id];
                actionLog?.addCommandData(
                    'variable',
                    'update',
                    data.variable_id,
                    variable &&
                        flowRevision.blocksMap[variable.connected_block_id]
                            ?.type
                );
            } else {
                const updatedFlowId = (<Partial<Flow>>data)._id;

                this.updateFlow(updatedFlowId, <Partial<Flow>>data);

                actionLog?.addCommandData('flow', 'update', updatedFlowId);
            }
        });

        // Delete
        deleteCommands.forEach(({ data }: IDeleteCommand) => {
            if (data.block_id) {
                flowRevision.deleteBlock(data.block_id);

                actionLog?.addCommandData(
                    'block',
                    'delete',
                    data.block_id,
                    flowRevision.blocksMap[data.block_id]?.type
                );
            } else if (data.page_id) {
                flowRevision.removePage(data.page_id);

                actionLog?.addCommandData(
                    'page',
                    'delete',
                    data.page_id,
                    flowRevision.pagesMap[data.page_id]?.title
                );
            }
        });
        flow.setDirty(true);
    }

    @action.bound
    loadTemplates(templates: Array<IEnrichedFlowTemplate>) {
        templates.forEach(template => {
            this.templates[template._id] = template;
        });
    }

    @action.bound
    removeTemplate(templateId: string) {
        if (this.templates[templateId]) {
            delete this.templates[templateId];
        }
    }

    @action.bound
    resetTemplates() {
        this.templates = {};
    }
}
